import { el, mount } from './dom';
import { t } from '@core/i18n';
import { getGame } from '@core/game-registry';
import { createPlayer, createTeam, defaultTurnOrder, validateConfig } from '@core/players';
import { navigate } from './router';
import { assetUrl } from './assets';
import type { GameConfig, GameMode, Player, Team, TurnEntity } from '@core/types';
import { saveSession } from '@core/session';
import { TurnManager } from '@core/turn-manager';

const TEAM_COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7', '#ec4899'];

export function renderSetup(gameId: string): HTMLElement {
  const game = getGame(gameId);
  if (!game) return el('div', { class: 'p-6' }, [`Unknown game: ${gameId}`]);

  let mode: GameMode = 'single';
  let players: Player[] = [createPlayer(t('common.you'))];
  let teams: Team[] = [];
  let turnOrder: TurnEntity[] = defaultTurnOrder({ mode, players, teams });
  let daily = false; // default casuale; l'utente può attivare la modalità daily dal toggle
  let error = '';

  const root = el('main', { class: 'mx-auto max-w-2xl p-4 sm:p-6', dataset: { theme: gameId } });

  function rebuildTurnOrder(): void {
    turnOrder = defaultTurnOrder({ mode, players, teams });
  }

  function moveOrder(idx: number, dir: -1 | 1): void {
    const j = idx + dir;
    if (j < 0 || j >= turnOrder.length) return;
    [turnOrder[idx], turnOrder[j]] = [turnOrder[j], turnOrder[idx]];
    render();
  }

  function entityLabel(e: TurnEntity): string {
    if (e.kind === 'team') return teams.find((t) => t.id === e.id)?.name ?? '?';
    return players.find((p) => p.id === e.id)?.name ?? '?';
  }

  async function start(): Promise<void> {
    const config: GameConfig = {
      mode,
      players,
      teams,
      turnOrder,
      daily,
    };
    const v = validateConfig(config, game!.metadata.minPlayers);
    if (!v.ok) {
      const code = v.error ?? '';
      if (code.startsWith('min_players:')) {
        error = t('setup.error.min_players', { n: code.split(':')[1] });
      } else if (code === 'empty_name') {
        error = t('setup.error.empty_name');
      } else {
        error = code;
      }
      render();
      return;
    }
    await game!.loadAssets();
    const sessionId = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    const tm = new TurnManager({
      gameType: game!.id,
      gameId: sessionId,
      config,
      module: game!,
    });
    await saveSession(tm.serialize());
    navigate({ name: 'play', gameId: game!.id, sessionId });
  }

  function render(): void {
    const modeButtons = (['single', 'multi-players', 'teams'] as GameMode[])
      .filter((m) => m !== 'teams' || game!.metadata.supportsTeams)
      .map((m) =>
        el(
          'button',
          {
            class: `btn ${mode === m ? 'btn-primary' : 'btn-ghost'}`,
            on: {
              click: () => {
                mode = m;
                if (m === 'single') players = players.slice(0, 1);
                if (m === 'teams' && teams.length === 0) {
                  teams = [
                    createTeam('Team A', TEAM_COLORS[0]),
                    createTeam('Team B', TEAM_COLORS[1]),
                  ];
                }
                rebuildTurnOrder();
                render();
              },
            },
          },
          [t(`setup.mode.${m}`)],
        ),
      );

    const playersList = el(
      'div',
      { class: 'grid gap-2' },
      players.map((p, idx) =>
        el('div', { class: 'flex gap-2 items-center' }, [
          el('input', {
            class: 'input flex-1',
            value: p.name,
            placeholder: t('setup.player_name'),
            on: {
              input: (ev) => {
                p.name = (ev.target as HTMLInputElement).value;
              },
            },
          }),
          mode === 'teams'
            ? el(
                'select',
                {
                  class: 'input max-w-[10rem]',
                  on: {
                    change: (ev) => {
                      p.teamId = (ev.target as HTMLSelectElement).value;
                    },
                  },
                },
                teams.map((tt) => {
                  const opt = el('option', { value: tt.id }, [tt.name]);
                  if (p.teamId === tt.id) opt.selected = true;
                  return opt;
                }),
              )
            : null,
          mode !== 'single' && players.length > 1
            ? el(
                'button',
                {
                  class: 'btn-ghost px-3',
                  on: {
                    click: () => {
                      players.splice(idx, 1);
                      rebuildTurnOrder();
                      render();
                    },
                  },
                },
                ['×'],
              )
            : null,
        ]),
      ),
    );

    const teamsBlock =
      mode === 'teams'
        ? el('section', { class: 'card' }, [
            el('h3', { class: 'font-semibold mb-3' }, [t('setup.teams')]),
            el(
              'div',
              { class: 'grid gap-2 mb-3' },
              teams.map((tt, idx) =>
                el('div', { class: 'flex gap-2 items-center' }, [
                  el('span', {
                    class: 'w-6 h-6 rounded-full',
                    style: `background:${tt.color}`,
                  }),
                  el('input', {
                    class: 'input flex-1',
                    value: tt.name,
                    placeholder: t('setup.team_name'),
                    on: {
                      input: (ev) => {
                        tt.name = (ev.target as HTMLInputElement).value;
                      },
                    },
                  }),
                  teams.length > 2
                    ? el(
                        'button',
                        {
                          class: 'btn-ghost px-3',
                          on: {
                            click: () => {
                              teams.splice(idx, 1);
                              players.forEach((p) => {
                                if (p.teamId === tt.id) p.teamId = teams[0]?.id;
                              });
                              rebuildTurnOrder();
                              render();
                            },
                          },
                        },
                        ['×'],
                      )
                    : null,
                ]),
              ),
            ),
            el(
              'button',
              {
                class: 'btn-ghost',
                on: {
                  click: () => {
                    teams.push(createTeam(`Team ${String.fromCharCode(65 + teams.length)}`, TEAM_COLORS[teams.length % TEAM_COLORS.length]));
                    rebuildTurnOrder();
                    render();
                  },
                },
              },
              [t('setup.add_team')],
            ),
          ])
        : null;

    const turnOrderBlock = el('section', { class: 'card' }, [
      el('h3', { class: 'font-semibold mb-1' }, [t('setup.turn_order')]),
      el('p', { class: 'text-xs text-slate-400 mb-3' }, [t('setup.turn_order.hint')]),
      el(
        'ol',
        { class: 'grid gap-2' },
        turnOrder.map((e, idx) =>
          el('li', { class: 'flex items-center gap-2 bg-slate-800 rounded-xl p-2' }, [
            el('span', { class: 'w-6 text-center text-slate-400' }, [`${idx + 1}.`]),
            el('span', { class: 'flex-1 font-medium' }, [entityLabel(e)]),
            el(
              'button',
              {
                class: 'btn-ghost px-2 py-1',
                disabled: idx === 0,
                on: { click: () => moveOrder(idx, -1) },
              },
              ['↑'],
            ),
            el(
              'button',
              {
                class: 'btn-ghost px-2 py-1',
                disabled: idx === turnOrder.length - 1,
                on: { click: () => moveOrder(idx, 1) },
              },
              ['↓'],
            ),
          ]),
        ),
      ),
    ]);

    const dailyToggle = game!.metadata.supportsDaily
      ? el('label', { class: 'flex items-center gap-2 text-sm text-slate-300' }, [
          el('input', {
            type: 'checkbox',
            checked: daily,
            on: {
              change: (ev) => {
                daily = (ev.target as HTMLInputElement).checked;
              },
            },
          }),
          t('setup.daily'),
        ])
      : null;

    const errorBox = error
      ? el('div', { class: 'rounded-xl bg-rose-900/40 text-rose-200 p-3 text-sm' }, [error])
      : null;

    mount(
      root,
      el('div', { class: 'grid gap-4' }, [
        el('header', { class: 'flex items-center justify-between' }, [
          el('button', { class: 'btn-ghost', on: { click: () => navigate({ name: 'lobby' }) } }, [
            '← ' + t('common.back'),
          ]),
          el('h1', { class: 'text-xl font-bold flex items-center gap-2' }, [
            game!.metadata.iconUrl
              ? (() => { const img = document.createElement('img'); img.src = assetUrl(game!.metadata.iconUrl); img.alt = ''; img.className = 'w-7 h-7 object-contain shrink-0'; return img; })()
              : el('span', {}, [game!.metadata.icon]),
            document.createTextNode(game!.metadata.name),
          ]),
          el('span', {}, ['']),
        ]),        el('div', { class: 'theme-divider' }),        el('section', { class: 'card flex flex-wrap gap-2' }, modeButtons),
        mode !== 'single'
          ? el('section', { class: 'card' }, [
              el('h3', { class: 'font-semibold mb-3' }, [t('setup.players')]),
              playersList,
              el(
                'button',
                {
                  class: 'btn-ghost mt-3',
                  on: {
                    click: () => {
                      const teamId = mode === 'teams' ? teams[players.length % teams.length]?.id : undefined;
                      players.push(createPlayer(`P${players.length + 1}`, teamId));
                      rebuildTurnOrder();
                      render();
                    },
                  },
                },
                [t('setup.add_player')],
              ),
            ])
          : el('section', { class: 'card' }, [playersList]),
        teamsBlock,
        mode !== 'single' ? turnOrderBlock : null,
        dailyToggle ? el('section', { class: 'card' }, [dailyToggle]) : null,
        errorBox,
        el('button', { class: 'btn-primary w-full', on: { click: () => void start() } }, [
          t('setup.start'),
        ]),
      ]),
    );
  }

  render();
  return root;
}
